import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Alert,
    Snackbar,
    Divider,
    Stack
} from '@mui/material';
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { createProduct, updateProduct } from '../Service/productServices';

const INITIAL_FORM_DATA = {
    name: '',
    description: '',
    price: '',
    serviceType: 'consultation',
    uom: 'service',
    billingCode: '',
    currency: 'EUR',
    taxpercent: '21',
    status: '1',
};

const SERVICE_TYPE_OPTIONS = [
    { value: 'consultation', label: 'Consultation' },
    { value: 'cita_medica', label: 'Medical Appointment' },
    { value: 'teleconsulta', label: 'Teleconsultation' },
    { value: 'despacho_medico', label: 'Medical Office' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'receta_electronica', label: 'Electronic Prescription' },
    { value: 'electronic_prescription', label: 'E-Prescription' },
    { value: 'orden_laboratorio', label: 'Laboratory Order' },
    { value: 'laboratory', label: 'Laboratory' },
    { value: 'imaging', label: 'Imaging' },
    { value: 'molecular', label: 'Molecular' },
    { value: 'procedimiento', label: 'Procedure' },
    { value: 'treatment', label: 'Treatment' },
    { value: 'vaccination', label: 'Vaccination' },
    { value: 'medical_record', label: 'Medical Record' },
    { value: 'otro', label: 'Other' },
];

const UOM_OPTIONS = [
    { value: 'service', label: 'Service' },
    { value: 'hour', label: 'Hour' },
    { value: 'session', label: 'Session' },
    { value: 'report', label: 'Report' },
    { value: 'visit', label: 'Visit' },
];

const CURRENCY_OPTIONS = [
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'USD', label: 'USD ($)' },
    { value: 'GBP', label: 'GBP (£)' },
];

const AddServiceForm = ({ open, onClose, selectedService, onServiceAdded, embedded = false }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const isEditing = useMemo(() => Boolean(selectedService?._id || selectedService?.id), [selectedService]);

    useEffect(() => {
        if (open) {
            if (selectedService) {
                setFormData({
                    name: selectedService.name || '',
                    description: selectedService.description || '',
                    price: selectedService.price?.toString() || '',
                    serviceType: selectedService.serviceType || 'consultation',
                    uom: selectedService.uom || 'service',
                    billingCode: selectedService.specifications?.billing_code || '',
                    currency: selectedService.currency || 'EUR',
                    taxpercent: selectedService.taxpercent?.toString() || '21',
                    status: selectedService.status?.toString() || '1',
                });
            } else {
                setFormData(INITIAL_FORM_DATA);
            }
            setErrors({});
        }
    }, [open, selectedService]);

    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'name':
                if (!value.trim()) {
                    newErrors.name = 'Name is required';
                } else if (value.length < 2) {
                    newErrors.name = 'Name must be at least 2 characters';
                } else {
                    delete newErrors.name;
                }
                break;

            case 'price':
                if (!value) {
                    newErrors.price = 'Price is required';
                } else if (parseFloat(value) <= 0) {
                    newErrors.price = 'Price must be greater than 0';
                } else {
                    delete newErrors.price;
                }
                break;

            case 'taxpercent':
                if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                    newErrors.taxpercent = 'Tax must be between 0 and 100';
                } else {
                    delete newErrors.taxpercent;
                }
                break;

            default:
                break;
        }

        setErrors(newErrors);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
        validateField(name, value);
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.price || parseFloat(formData.price) <= 0) {
            newErrors.price = 'Price is required and must be greater than 0';
        }
        if (formData.taxpercent && (parseFloat(formData.taxpercent) < 0 || parseFloat(formData.taxpercent) > 100)) {
            newErrors.taxpercent = 'Tax must be between 0 and 100';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const generateServiceCode = (serviceType) => {
        const prefix = serviceType.substring(0, 3).toUpperCase();
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 1000);
        return `SRV-${prefix}-${timestamp}-${random}`;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            showNotification('Please correct the errors in the form', 'error');
            return;
        }

        setLoading(true);
        try {
            const serviceData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                type: 'service',
                serviceType: formData.serviceType,
                price: parseFloat(formData.price),
                uom: formData.uom,
                currency: formData.currency,
                taxpercent: parseFloat(formData.taxpercent),
                status: formData.status,
                isActive: formData.status === '1',
                specifications: {
                    billing_code: formData.billingCode.trim()
                }
            };

            let result;
            if (isEditing) {
                const id = selectedService._id || selectedService.id;
                result = await updateProduct(id, serviceData);
            } else {
                // Generar ID y código para nuevo servicio
                const code = generateServiceCode(formData.serviceType);
                serviceData.id = code;
                serviceData.code = code;
                result = await createProduct(serviceData);
            }

            if (result) {
                onServiceAdded(result);
                showNotification(
                    isEditing ? 'Service updated successfully' : 'Service created successfully',
                    'success'
                );
                setTimeout(() => onClose(), 1500);
            }
        } catch (error) {
            console.error('Error saving service:', error);
            showNotification('Error saving service. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleLocalClose = () => {
        if (embedded) return; // Si está embebido, el cierre se maneja desde el padre
        
        if (JSON.stringify(formData) !== JSON.stringify(INITIAL_FORM_DATA) && !isEditing) {
            if (window.confirm('Are you sure you want to close? Unsaved changes will be lost.')) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const ContentWrapper = embedded ? React.Fragment : Box;
const wrapperProps = embedded ? {} : { 
    sx: { 
        width: 450, 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        maxHeight: '100vh' // Añadido
    } 
};

return (
    <>
        <ContentWrapper {...wrapperProps}>
            {/* Content */}
            <Box sx={{ 
                flex: 1, 
                overflow: 'auto', 
                overflowX: 'hidden', // Añadido para evitar scroll horizontal
                p: 3,
                maxHeight: embedded ? 'calc(100vh - 200px)' : 'calc(100vh - 180px)' // Añadido
            }}>
                    <Stack spacing={3}>
                        {/* Información básica */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Basic Information
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth
                                    label="Service Name *"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                />

                                <TextField
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    multiline
                                    rows={3}
                                />

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Service Type *</InputLabel>
                                            <Select
                                                label="Service Type *"
                                                name="serviceType"
                                                value={formData.serviceType}
                                                onChange={handleChange}
                                            >
                                                {SERVICE_TYPE_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Unit of Measure</InputLabel>
                                            <Select
                                                label="Unit of Measure"
                                                name="uom"
                                                value={formData.uom}
                                                onChange={handleChange}
                                            >
                                                {UOM_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Box>

                        <Divider />

                        {/* Pricing */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Pricing & Tax
                            </Typography>
                            <Stack spacing={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Price *"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleChange}
                                            type="number"
                                            error={!!errors.price}
                                            helperText={errors.price}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">€</InputAdornment>
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Currency</InputLabel>
                                            <Select
                                                label="Currency"
                                                name="currency"
                                                value={formData.currency}
                                                onChange={handleChange}
                                            >
                                                {CURRENCY_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <TextField
                                    fullWidth
                                    label="Tax Percentage"
                                    name="taxpercent"
                                    value={formData.taxpercent}
                                    onChange={handleChange}
                                    type="number"
                                    error={!!errors.taxpercent}
                                    helperText={errors.taxpercent}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                                    }}
                                />
                            </Stack>
                        </Box>

                        <Divider />

                        {/* Additional Info */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Additional Information
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth
                                    label="Billing Code"
                                    name="billingCode"
                                    value={formData.billingCode}
                                    onChange={handleChange}
                                    placeholder="e.g. REC-REPORT-001"
                                />

                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        label="Status"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="1">Active</MenuItem>
                                        <MenuItem value="0">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>

                {/* Footer */}
                <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            onClick={handleLocalClose}
                            startIcon={<CancelIcon />}
                            fullWidth
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<SaveIcon />}
                            fullWidth
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (isEditing ? 'Update' : 'Save')}
                        </Button>
                    </Stack>
                </Box>
            </ContentWrapper>

            {/* Notificaciones */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification({ ...notification, open: false })}
            >
                <Alert
                    severity={notification.severity}
                    onClose={() => setNotification({ ...notification, open: false })}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddServiceForm;